import { isNativePlatform } from 'components/helper/Platform';
import { EventEmitter } from 'events';
import { log, warn } from 'helper/debug';

let sharedEventsAttached = false;

export const SHARED_EVENTS_ID = '__REACT_SHARED_EVENTS__';

export interface ISharedEmitterAPI {
	emit(event: string, ...args: Array<any>): boolean;
	on(event: string, listener: Function): ISharedEmitterAPI;
	once(event: string, listener: Function): ISharedEmitterAPI;
}

function identity<T>(ident: T): T {
	return ident;
}

class FakeEventEmitter implements ISharedEmitterAPI {
	public addListener = () => identity(this);
	public emit = () => identity(false);
	public listenerCount = () => identity(-1);
	public on = () => identity(this);
	public once = () => identity(this);
	public removeAllListeners = () => identity(this);
	public removeListener = () => identity(this);
	public setMaxListeners = () => identity(this);
}

// tslint:disable-next-line: max-classes-per-file
class SharedEventEmitter implements ISharedEmitterAPI {
	private internal: EventEmitter = new EventEmitter();

	constructor() {
		this.internal.setMaxListeners(127);
	}

	public on(event: string, listener: any): this {
		log(`SharedEvents: Register listener for ${event}`);
		this.internal.on(event, listener);
		return this;
	}

	public once(event: string, listener: any): this {
		log(`SharedEvents: Register one-time listener for ${event}`);
		this.internal.once(event, listener);
		return this;
	}

	public emit(event: string, ...args: any[]): boolean {
		try {
			log(`SharedEvents: Emitting event "${event}" with arguments ${JSON.stringify(args)}`);
			this.internal.emit(event, ...args);
			return true;
		} catch (e) {
			warn(
				[
					`SharedEvents: Emitting ${event} caused an internal issue:`,
					e,
					`with arguments ${JSON.stringify(args)}`,
					`at event "${event}"`,
					`in lib/SharedEvents.js`,
				].join(`\n\t`)
			);
			return false;
		}
	}
}

const register = () => {
	window[SHARED_EVENTS_ID] = isNativePlatform() ? new SharedEventEmitter() : new FakeEventEmitter();
	sharedEventsAttached = true;
};

const attachAsync = (): Promise<ISharedEmitterAPI> => {
	return new Promise((resolve: Function, reject: Function) => {
		if (!sharedEventsAttached) {
			reject(`SharedEvents: You must first register the shared event dispatcher before attaching to it.`);
		}

		resolve(window[SHARED_EVENTS_ID]);
	});
};

const attach = (): ISharedEmitterAPI => window[SHARED_EVENTS_ID] || new FakeEventEmitter();

export { register, attach, attachAsync };

export default attach;
